import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Monitor, Server, Laptop, Smartphone, ChevronRight, Loader2, Building2, Search } from 'lucide-react';
import { configurationsAPI } from '../services/api';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';

export default function ConfigurationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastRefreshTimestamp = useRef(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');

  // Multi-company support
  const { selectedCompanyId, hasMultipleCompanies } = useCompanyStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['configurations', selectedCompanyId],
    queryFn: () => configurationsAPI.list({ companyId: selectedCompanyId }),
  });

  // Sync search to URL so it survives back-navigation from the detail page.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [search, setSearchParams]);

  // Auto-refresh when returning from detail page
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      setSearch(searchParams.get('search') || '');
      refetch();
    }
  }, [location.state?.refresh, refetch, searchParams]);

  const allConfigs = data?.data?.configurations || [];

  // Filter by search
  const configs = allConfigs.filter(config => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return config.name?.toLowerCase().includes(searchLower) ||
           config.company?.name?.toLowerCase().includes(searchLower) ||
           config.type?.name?.toLowerCase().includes(searchLower) ||
           config.serialNumber?.toLowerCase().includes(searchLower);
  });
  
  const getIcon = (type) => {
    const typeName = type?.name?.toLowerCase() || '';
    if (typeName.includes('server')) return Server;
    if (typeName.includes('laptop')) return Laptop;
    if (typeName.includes('mobile') || typeName.includes('phone')) return Smartphone;
    return Monitor;
  };

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Assets & Configurations</h1>
          <p className="text-neutral-600 dark:text-neutral-400">View your managed devices and assets</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search assets..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <CompanyFilter />
      </div>
      
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              {hasMultipleCompanies && <th>Company</th>}
              <th>Type</th>
              <th>Status</th>
              <th>Serial Number</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5 + (hasMultipleCompanies ? 1 : 0)} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto mb-2" />
                  <span className="text-neutral-600 dark:text-neutral-400">Loading assets...</span>
                </td>
              </tr>
            ) : configs.length === 0 ? (
              <tr><td colSpan={5 + (hasMultipleCompanies ? 1 : 0)} className="text-center py-8 text-neutral-500 dark:text-neutral-400">No assets found</td></tr>
            ) : (
              configs.map((config) => {
                const Icon = getIcon(config.type);
                return (
                  <tr
                    key={config.id}
                    onClick={() => {
                      sessionStorage.setItem('configurationsReturnUrl', window.location.pathname + window.location.search);
                      navigate(`/assets/${config.id}`);
                    }}
                    className="cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors"
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-neutral-400" />
                        <span className="font-medium">{config.name}</span>
                      </div>
                    </td>
                    {hasMultipleCompanies && (
                      <td>
                        <span className="inline-flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-400">
                          <Building2 className="w-3 h-3" />
                          {config.company?.name || 'N/A'}
                        </span>
                      </td>
                    )}
                    <td>{config.type?.name || 'N/A'}</td>
                    <td>
                      <span className={`badge ${config.status?.name === 'Active' ? 'badge-success' : 'badge-neutral'}`}>
                        {config.status?.name || 'Unknown'}
                      </span>
                    </td>
                    <td className="font-mono text-sm">{config.serialNumber || 'N/A'}</td>
                    <td className="text-right">
                      <ChevronRight className="w-5 h-5 text-neutral-400 inline-block" />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
